import React from 'react'
import { Box } from '@mui/material'

/**
 * Exactly one of `onClick` or `href` — never both, never neither.
 *
 * A union rather than two optional props, because "neither" is a link that does
 * nothing and "both" is a link with two conflicting destinations, and both of
 * those should be compile errors rather than something a reader has to notice.
 */
export type AuthInlineLinkProps = {
  children: React.ReactNode
  /**
   * The link stands on its OWN line (a stack of options under a form — "Use a recovery code
   * instead", "Resend code") rather than inside a sentence. Plain at rest, underlined on
   * hover and on keyboard focus: the house treatment for a secondary auth action, the same
   * as sign-in's "Forgot password?". See STANDALONE_LINK_SX for why a sentence link keeps
   * its underline.
   */
  standalone?: boolean
} & (
  | { onClick: () => void; href?: never }
  | { href: string; onClick?: never }
)

/**
 * A link that sits INSIDE a sentence — "Don't have an account? Create one",
 * "Already have an account? Sign in".
 *
 * WHY IT EXISTS (VERA-1095). It is a `<button>`, because it runs the auth
 * cross-fade rather than navigating (see `useAuthPageTransition`), and a
 * `<button>` dropped into a paragraph does NOT sit on the surrounding text's
 * baseline: browsers default it to `vertical-align: middle`. On the sign-in
 * page that put "Create one" **3.25px** below the sentence it belongs to —
 * measured, not estimated. Font size and line-height already matched (14px /
 * 19.6px), so alignment was the only defect and `verticalAlign: 'baseline'`
 * is the whole fix.
 *
 * It is a shared component rather than a per-page `sx` because this was the
 * SECOND hand-roll of the same thing (working-conventions §36): sign-in used
 * MUI `Link component="button"`, sign-up used a bare `Box component="button"`
 * with its own reset. They had drifted — only one reset `font`, only one had a
 * hover colour, and neither fixed the baseline. One component means the next
 * inline link cannot reintroduce the misalignment.
 *
 * `font: 'inherit'` is load-bearing and must stay a shorthand: it resets
 * family, size, weight AND line-height in one declaration, so the link cannot
 * drift from its sentence when a caller changes the surrounding Typography.
 * `color: 'inherit'` matches the sentence; the underline is what marks it as a
 * link, and hover promotes it to `text.primary` so the affordance is not
 * carried by colour alone at rest.
 *
 * THE `href` VARIANT (VRG-595). Some inline links on these pages go OUT rather
 * than across — `mailto:support@vera.inc` on the signup refusal and on the
 * invited-email helper. Those must be real anchors: a `<button>` with an
 * onClick cannot be opened in a new tab, copied via the context menu, or read
 * as a link by a screen reader, and for an address the reader may want to copy
 * rather than click, that matters more than usual.
 *
 * Added HERE rather than reaching for MUI's `Link` at those two call sites
 * (§47 — improve the shared piece rather than build beside it). A bare `Link`
 * would have introduced a SECOND link treatment onto the auth pages: it takes
 * its colour from `primary.main` instead of the sentence, and it carries none
 * of the baseline fix, the `font: inherit` reset or the focus ring above. The
 * two variants share one `sx` so the anchor cannot drift from the button.
 *
 * `color: 'inherit'` is what makes this safe inside an `Alert` description,
 * which sets its own muted token — the link tracks whatever colour the
 * sentence around it is using, in either theme, and the underline carries the
 * affordance.
 */
const INLINE_LINK_SX = {
  // The fix. Everything below is the reset that makes a <button> stop
  // looking like one. (The anchor needs most of the same reset — a UA
  // styles `a` with its own colour and underline — so the two share it.)
  verticalAlign: 'baseline',
  background: 'none',
  border: 0,
  padding: 0,
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  textDecoration: 'underline',
  '&:hover': { color: 'text.primary' },
  // Keyboard parity: without this the focus ring is the browser default,
  // which on a transparent inline button is easy to lose against the page.
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'brand.600',
    outlineOffset: '2px',
    borderRadius: '2px',
  },
} as const

/**
 * Spencer, 2026-10-11, on the 2FA screen: "Use a recovery code" and "Use a one-time SMS
 * password" were BOTH underlined all the time — two underlines stacked under the Verify
 * button read as noise. A standalone option is already marked as an action by sitting on
 * its own line under the form, so it drops the resting underline and takes it on hover
 * (with the colour lift) and on `:focus-visible`, which keeps a non-colour cue for keyboard
 * users on top of the focus ring.
 *
 * A link INSIDE a sentence keeps the resting underline: it inherits the sentence's colour,
 * so without the underline nothing would distinguish it from the words around it
 * (WCAG 1.4.1, use of colour).
 */
const STANDALONE_LINK_SX = {
  ...INLINE_LINK_SX,
  textDecoration: 'none',
  '&:hover': { color: 'text.primary', textDecoration: 'underline' },
  '&:focus-visible': { ...INLINE_LINK_SX['&:focus-visible'], textDecoration: 'underline' },
} as const

export const AuthInlineLink = ({ onClick, href, children, standalone = false }: AuthInlineLinkProps) => {
  const sx = standalone ? STANDALONE_LINK_SX : INLINE_LINK_SX
  // Two explicit branches rather than `component={href ? 'a' : 'button'}`: a
  // dynamic `component` erases Box's element-prop typing, so `type` and `href`
  // would both stop being checked against the element that actually renders.
  if (href !== undefined) {
    return (
      <Box component="a" href={href} sx={sx}>
        {children}
      </Box>
    )
  }

  return (
    <Box component="button" type="button" onClick={onClick} sx={sx}>
      {children}
    </Box>
  )
}

export default AuthInlineLink
